/**
 * Flatten an adjacency-list tree into the rows a tree view renders.
 *
 * Pure so the expand/collapse, ordering and range-selection rules are testable
 * without a DOM. Ordering follows `sort_key` within a parent, the same order the
 * packet uses, so the tree and the exhibit labels always agree.
 *
 * Generic over the node shape: the Case Files tree (`FsNode`) and the Case
 * Workspace tree (`WsNode`) share the same `parent_id` + `sort_key` layout and
 * both use `kind: "folder"` for containers, so they share these helpers.
 */

/** What every tree node must carry for the helpers here to order and nest it. */
export type TreeNodeLike = {
  id: string;
  parent_id: string | null;
  kind: string;
  sort_key: string;
  is_deleted?: boolean;
};

export type TreeRow<N extends TreeNodeLike = TreeNodeLike> = {
  node: N;
  depth: number;
  hasChildren: boolean;
  expanded: boolean;
};

const bySortKey = <N extends TreeNodeLike>(a: N, b: N) =>
  a.sort_key < b.sort_key ? -1 : a.sort_key > b.sort_key ? 1 : 0;

export function childrenByParent<N extends TreeNodeLike>(
  nodes: N[]
): Map<string | null, N[]> {
  const grouped = new Map<string | null, N[]>();
  nodes.forEach((node) => {
    if (node.is_deleted) return;
    const key = node.parent_id ?? null;
    const list = grouped.get(key);
    if (list) list.push(node);
    else grouped.set(key, [node]);
  });
  grouped.forEach((list) => list.sort(bySortKey));
  return grouped;
}

export function visibleRows<N extends TreeNodeLike>(
  nodes: N[],
  expanded: ReadonlySet<string>,
  /** Top-level nodes listed after every other top-level node. */
  pinLast?: (node: N) => boolean
): TreeRow<N>[] {
  const grouped = childrenByParent(nodes);
  const rows: TreeRow<N>[] = [];
  const walk = (parentId: string | null, depth: number) => {
    const siblings = grouped.get(parentId) ?? [];
    const ordered =
      parentId === null && pinLast
        ? [
            ...siblings.filter((n) => !pinLast(n)),
            ...siblings.filter((n) => pinLast(n))
          ]
        : siblings;
    for (const node of ordered) {
      const kids = grouped.get(node.id) ?? [];
      const isOpen = node.kind === "folder" && expanded.has(node.id);
      rows.push({
        node,
        depth,
        hasChildren: kids.length > 0,
        expanded: isOpen
      });
      if (isOpen) walk(node.id, depth + 1);
    }
  };
  walk(null, 0);
  return rows;
}

/** Ids of every ancestor folder of `nodeId`, nearest first. */
export function ancestorsOf<N extends TreeNodeLike>(
  nodes: N[],
  nodeId: string
): string[] {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const out: string[] = [];
  let current = byId.get(nodeId)?.parent_id ?? null;
  while (current) {
    out.push(current);
    current = byId.get(current)?.parent_id ?? null;
  }
  return out;
}

/** Ids of `nodeId` and everything below it. */
export function subtreeIds<N extends TreeNodeLike>(
  nodes: N[],
  nodeId: string
): Set<string> {
  const grouped = childrenByParent(nodes);
  const out = new Set<string>([nodeId]);
  const stack = [nodeId];
  while (stack.length) {
    const id = stack.pop() as string;
    for (const child of grouped.get(id) ?? []) {
      out.add(child.id);
      stack.push(child.id);
    }
  }
  return out;
}

/** A folder cannot be dropped into itself or a descendant. */
export function canDropInto<N extends TreeNodeLike>(
  nodes: N[],
  movingIds: string[],
  targetFolderId: string | null
): boolean {
  if (targetFolderId === null) return true;
  const target = nodes.find((n) => n.id === targetFolderId);
  if (!target || target.kind !== "folder") return false;
  for (const id of movingIds) {
    if (id === targetFolderId) return false;
    if (subtreeIds(nodes, id).has(targetFolderId)) return false;
  }
  return true;
}

/** Ids between two rows (inclusive) in visible order, for shift-click. */
export function rangeBetween<N extends TreeNodeLike>(
  rows: TreeRow<N>[],
  fromId: string,
  toId: string
): string[] {
  const a = rows.findIndex((r) => r.node.id === fromId);
  const b = rows.findIndex((r) => r.node.id === toId);
  if (a < 0 || b < 0) return [toId];
  const [lo, hi] = a < b ? [a, b] : [b, a];
  return rows.slice(lo, hi + 1).map((r) => r.node.id);
}

/** Next/previous row id for arrow keys, or null at the ends. */
export function stepRow<N extends TreeNodeLike>(
  rows: TreeRow<N>[],
  currentId: string | null,
  delta: 1 | -1
): string | null {
  if (rows.length === 0) return null;
  const index = currentId ? rows.findIndex((r) => r.node.id === currentId) : -1;
  if (index < 0)
    return delta > 0 ? rows[0].node.id : rows[rows.length - 1].node.id;
  const next = rows[index + delta];
  return next ? next.node.id : null;
}

/** Where a drop lands relative to a row: inside the folder or between siblings. */
export type DropPlacement =
  | { kind: "inside"; parentId: string | null }
  | { kind: "before"; siblingId: string; parentId: string | null }
  | { kind: "after"; siblingId: string; parentId: string | null };

export function placementFor<N extends TreeNodeLike>(
  row: TreeRow<N>,
  /** Cursor y within the row, 0..1. */
  ratio: number
): DropPlacement {
  const parentId = row.node.parent_id ?? null;
  if (row.node.kind === "folder") {
    if (ratio < 0.25)
      return { kind: "before", siblingId: row.node.id, parentId };
    if (ratio > 0.75) {
      // Dropping under an expanded folder means "first child".
      return row.expanded
        ? { kind: "inside", parentId: row.node.id }
        : { kind: "after", siblingId: row.node.id, parentId };
    }
    return { kind: "inside", parentId: row.node.id };
  }
  return ratio < 0.5
    ? { kind: "before", siblingId: row.node.id, parentId }
    : { kind: "after", siblingId: row.node.id, parentId };
}
